import api from "@/services/api";

/**
 * @description Servicio para interactuar con el endpoint /plan-coverages.
 * Gestiona las coberturas de prestaciones asociadas a un plan.
 */
const planCoverageService = {};

const endpoint = "/plan-coverages";

/**
 * @description Obtiene listado paginado de coberturas de un plan
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.plan - ID del plan (obligatorio)
 * @param {string} [params.searchInput] - Texto de búsqueda
 * @param {number} [params.offset] - Offset para paginación
 * @param {number} [params.limit] - Límite de resultados
 * @returns {Promise<{docs: Array, total: number}>} Resultado paginado
 */
planCoverageService.query = function (params) {
  return api.get(endpoint, { params }).then((res) => res.data);
};

/**
 * @description Obtiene una cobertura por su ID
 * @param {string} id - ID de la cobertura
 * @returns {Promise<Object>} Datos de la cobertura
 */
planCoverageService.getById = function (id) {
  return api.get(`${endpoint}/${id}`).then((res) => res.data);
};

/**
 * @description Crea una nueva cobertura para un plan
 * @param {Object} data - Datos de la cobertura
 * @param {string} data.plan - ID del plan
 * @param {string} data.practice - ID de la prestación (practice-catalog)
 * @param {number} [data.coveragePercent] - % de cobertura
 * @param {number} [data.copayAmount] - Copago fijo $
 * @param {number} [data.copayPercent] - Copago %
 * @param {boolean} [data.requiresAuthorization] - Requiere autorización
 * @param {number} [data.annualLimit] - Tope anual
 * @param {number} [data.monthlyLimit] - Tope mensual
 * @param {number} [data.waitingDays] - Carencia en días
 * @returns {Promise<Object>} Cobertura creada
 */
planCoverageService.create = function (data) {
  return api.post(endpoint, data).then((res) => res.data);
};

/**
 * @description Actualiza una cobertura existente
 * @param {string} id - ID de la cobertura
 * @param {Object} data - Campos a actualizar
 * @returns {Promise<Object>} Cobertura actualizada
 */
planCoverageService.update = function (id, data) {
  return api.put(`${endpoint}/${id}`, data).then((res) => res.data);
};

/**
 * @description Elimina una cobertura
 * @param {string} id - ID de la cobertura
 * @returns {Promise<void>}
 */
planCoverageService.remove = function (id) {
  return api.delete(`${endpoint}/${id}`);
};

/**
 * @description Importa coberturas desde archivo Excel/CSV
 * @param {string} planId - ID del plan destino
 * @param {File} file - Archivo a importar (.xlsx, .xls, .csv)
 * @returns {Promise<{imported: number, duplicates: number, total: number, errors: Array}>} Resultado
 */
planCoverageService.import = function (planId, file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("plan", planId);
  return api
    .post(`${endpoint}/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

/**
 * @description Verifica si una prestación está cubierta por un plan
 * @param {string} planId - ID del plan
 * @param {string} practiceId - ID de la prestación
 * @returns {Promise<Object>} Datos de cobertura o null
 */
planCoverageService.check = function (planId, practiceId) {
  return api
    .get(`${endpoint}/check`, {
      params: { plan: planId, practice: practiceId },
    })
    .then((res) => res.data);
};

/**
 * @description Importa prestaciones del catálogo maestro al plan, pre-completando cobertura desde datos PMO
 * @param {string} planId - ID del plan destino
 * @param {string} organizationId - ID de la organización
 * @returns {Promise<{imported: number, skipped: number}>} Resultado de la importación
 */
planCoverageService.importFromCatalog = function (planId, organizationId) {
  return api
    .post(`${endpoint}/import-from-catalog`, {
      plan: planId,
      organization: organizationId,
    })
    .then((res) => res.data);
};

export default planCoverageService;
